import type { ReactNode } from "react";

import { Skeleton } from "@/v2/components/ui/skeleton";
import { cn } from "@/v2/lib/utils";

type EntrySkeletonVariant = "long" | "medium" | "short";

interface EntriesSkeletonGroupProps {
	children: ReactNode;
}

interface EntryCardSkeletonProps {
	variant: EntrySkeletonVariant;
	showCodes?: boolean;
}

export function EntriesLoadingSkeleton() {
	return (
		<>
			<EntriesSkeletonGroup>
				<EntryCardSkeleton variant="long" showCodes />
				<EntryCardSkeleton variant="medium" showCodes />
				<EntryCardSkeleton variant="short" />
			</EntriesSkeletonGroup>

			<EntriesSkeletonGroup>
				<EntryCardSkeleton variant="medium" showCodes />
				<EntryCardSkeleton variant="short" />
			</EntriesSkeletonGroup>
		</>
	);
}

function EntriesSkeletonGroup({ children }: EntriesSkeletonGroupProps) {
	return (
		<div>
			<div className="border-b border-border px-4 py-1.5">
				<Skeleton className="h-3 w-16" />
			</div>
			{children}
		</div>
	);
}

function EntryCardSkeleton({ variant, showCodes }: EntryCardSkeletonProps) {
	const matterWidth = variant === "long" ? "w-40" : "w-32";
	const clientWidth = variant === "short" ? "w-20" : "w-32";
	const showThirdLine = variant === "long";

	return (
		<div className="flex min-h-[86px] items-start gap-3 border-b border-border px-4 py-2.5">
			<Skeleton className="mt-0.5 size-7 shrink-0 rounded-md" />
			<div className="min-w-0 flex-1">
				<div className="flex items-baseline justify-between gap-2">
					<Skeleton className={cn("h-3.5", matterWidth)} />
					<Skeleton className="h-3 w-14 shrink-0" />
				</div>
				<div className="mt-1 flex items-center justify-between gap-2">
					<Skeleton className={cn("h-3", clientWidth)} />
					<Skeleton className="h-3 w-10 shrink-0" />
				</div>
				<div className="mt-2 space-y-1">
					<Skeleton className="h-3 w-full" />
					<Skeleton className="h-3 w-11/12" />
					{showThirdLine && <Skeleton className="h-3 w-3/4" />}
				</div>
				{showCodes && (
					<div className="mt-1.5 flex items-center gap-1.5">
						<Skeleton className="h-[18px] w-9 rounded-full" />
						<Skeleton className="h-[18px] w-10 rounded-full" />
					</div>
				)}
			</div>
		</div>
	);
}
